import { useMemo, useState } from "react";

import { labelFromId } from "./shared";
import { projectLabelFromId } from "../app/format";
import {
  TechnicalDetails,
  type TechnicalDetailsEntry
} from "../components/TechnicalDetails";
import type {
  RequirementStudioDisplayDocument,
  RequirementStudioDisplayViewModel
} from "../viewmodel/requirementStudioAdapter";

const documentKindTone: Record<string, { bg: string; text: string }> = {
  XLSX: { bg: "var(--success-bg)", text: "var(--success)" },
  PDF: { bg: "var(--risk-bg)", text: "var(--risk)" },
  // C4-W2 fix-2: were fixed literals; var(--network*) keeps these AA in
  // dark theme (index.css) with no light-theme change.
  CALL: { bg: "var(--network-bg)", text: "var(--network)" },
  NOTE: { bg: "var(--chip)", text: "var(--muted2)" },
  DWG: { bg: "var(--info-bg)", text: "var(--info)" },
  SPEC: { bg: "var(--network-bg)", text: "var(--network)" },
  TAKEOFF: { bg: "var(--success-bg)", text: "var(--success)" }
};

const documentStatusTone = {
  ok: { bg: "var(--success-bg)", text: "var(--success)" },
  review: { bg: "var(--warn-bg)", text: "var(--warn)" },
  missing: { bg: "var(--risk-bg)", text: "var(--risk)" },
  quiet: { bg: "var(--chip)", text: "var(--muted2)" }
} as const;

const plainDocumentStatusLabel = (label: string): string =>
  label === "Not produced" ? "Not available yet" : label;

const plainStatLabel = (label: string): string => {
  if (label === "Requirements extracted") {
    return "Requirements found";
  }

  if (label === "Approved by policy") {
    return "Approved by rules";
  }

  if (label === "Requirement source links") {
    return "Source links";
  }

  return label;
};

const plainStatValue = (value: string): string =>
  value === "Not yet produced" ? "Not available yet" : value;

const plainDocumentMeta = (meta: string): string =>
  meta
    .replace("passed to runProject(specText)", "used by the AI")
    .replace("passed to Lane A", "used by the AI");

const documentMetaParts = (
  document: RequirementStudioDisplayDocument,
  updatedAt: string
): string[] => [
  ...plainDocumentMeta(document.meta)
    .split(/\s*[·/]\s*/g)
    .map((part) => part.trim())
    .filter(Boolean),
  `${document.sourceRefCount} source link${document.sourceRefCount === 1 ? "" : "s"}`,
  `Captured ${formatDocumentDate(updatedAt)}`
];

const documentBadges = (document: RequirementStudioDisplayDocument): string[] => [
  "Real project input",
  ...(isOverrideDocument(document) ? ["OVERRIDE"] : [])
];

const isOverrideDocument = (document: RequirementStudioDisplayDocument): boolean =>
  document.kind === "NOTE" ||
  /\boverride\b/i.test(`${document.name} ${document.meta} ${document.summary}`);

const formatDocumentDate = (isoDate: string): string => {
  const date = new Date(isoDate);

  if (Number.isNaN(date.getTime())) {
    return "latest run";
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short"
  }).format(date);
};

const documentKindLabel = (kind: string): string =>
  labelFromId(kind.toLowerCase().replaceAll("_", "-"));

const inputDocumentFilenameStem = (
  document: RequirementStudioDisplayDocument
): string =>
  document.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "input-document";

export const inputDocumentDownloadName = (
  document: RequirementStudioDisplayDocument
): string => `${inputDocumentFilenameStem(document)}-summary.txt`;

export const inputDocumentRawDownloadName = (
  document: RequirementStudioDisplayDocument
): string => `${inputDocumentFilenameStem(document)}-raw.json`;

const inputDocumentTechnicalSources = (
  document: RequirementStudioDisplayDocument,
  updatedAt: string
): TechnicalDetailsEntry[] => [
  {
    label: document.name,
    detail: `${document.sourceRefCount} source link${document.sourceRefCount === 1 ? "" : "s"} retained for this input.`,
    meta: `Captured ${formatDocumentDate(updatedAt)}`
  }
];

const inputDocumentTechnicalChecks = (
  document: RequirementStudioDisplayDocument
): TechnicalDetailsEntry[] => [
  {
    label: plainDocumentStatusLabel(document.statusLabel),
    detail: `${document.requirementCount} requirement${document.requirementCount === 1 ? "" : "s"} linked to this input.`,
    tone:
      document.statusTone === "ok"
        ? "success"
        : document.statusTone === "missing"
          ? "risk"
          : document.statusTone === "review"
            ? "warning"
            : "neutral"
  }
];

export const inputDocumentSummaryText = (
  document: RequirementStudioDisplayDocument,
  updatedAt: string
): string => {
  const lines = [
    document.name,
    "",
    `Document type: ${documentKindLabel(document.kind)}`,
    `Status: ${plainDocumentStatusLabel(document.statusLabel)}`,
    `Captured: ${formatDocumentDate(updatedAt)}`,
    `Source links: ${document.sourceRefCount}`,
    `Requirements linked: ${document.requirementCount}`,
    "",
    "Summary",
    document.summary,
    "",
    "How it is used",
    plainDocumentMeta(document.meta)
  ];

  return `${lines.join("\n")}\n`;
};

export const inputDocumentRawJson = (
  document: RequirementStudioDisplayDocument,
  updatedAt: string
): string =>
  `${JSON.stringify(
    {
      id: document.id,
      kind: document.kind,
      name: document.name,
      meta: document.meta,
      status: document.status,
      statusLabel: document.statusLabel,
      statusTone: document.statusTone,
      sourceRefCount: document.sourceRefCount,
      requirementCount: document.requirementCount,
      dataGap: document.dataGap,
      summary: document.summary,
      capturedAt: updatedAt
    },
    null,
    2
  )}\n`;

const downloadTextFile = (
  filename: string,
  contents: string,
  mimeType: string
): void => {
  if (typeof window === "undefined" || typeof Blob === "undefined") {
    return;
  }

  const urlApi = window.URL;
  const anchor = window.document.createElement("a");
  const blob = new Blob([contents], { type: mimeType });
  const url = urlApi.createObjectURL(blob);

  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  urlApi.revokeObjectURL(url);
};

const downloadInputDocument = (
  document: RequirementStudioDisplayDocument,
  updatedAt: string
): void => {
  downloadTextFile(
    inputDocumentDownloadName(document),
    inputDocumentSummaryText(document, updatedAt),
    "text/plain;charset=utf-8"
  );
};

const downloadInputDocumentRawData = (
  document: RequirementStudioDisplayDocument,
  updatedAt: string
): void => {
  downloadTextFile(
    inputDocumentRawDownloadName(document),
    inputDocumentRawJson(document, updatedAt),
    "application/json;charset=utf-8"
  );
};

export function InputsScreen({
  view,
  selectedApartmentTypeId,
  onOpenTender
}: {
  view: RequirementStudioDisplayViewModel;
  selectedApartmentTypeId: string;
  onOpenTender: () => void;
}) {
  const projectLabel = projectLabelFromId(view.projectId);
  const [removedDocumentIds, setRemovedDocumentIds] = useState<Set<string>>(
    () => new Set()
  );
  const [pendingDeleteDocument, setPendingDeleteDocument] =
    useState<RequirementStudioDisplayDocument | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const documents = useMemo(
    () => view.documents.filter((document) => !removedDocumentIds.has(document.id)),
    [removedDocumentIds, view.documents]
  );
  const removeDocument = (documentId: string): void => {
    setRemovedDocumentIds((current) => new Set(current).add(documentId));
    setPendingDeleteDocument(null);
    setDeleteConfirmation("");
  };
  const requestDocumentDelete = (document: RequirementStudioDisplayDocument): void => {
    setPendingDeleteDocument(document);
    setDeleteConfirmation("");
  };
  const cancelDocumentDelete = (): void => {
    setPendingDeleteDocument(null);
    setDeleteConfirmation("");
  };

  return (
    <div className="flex-1 overflow-auto px-8 py-[34px] pb-[76px] xl:px-14">
      <div className="mx-auto max-w-[1080px]">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
              Step 1 · what we've been given
            </div>
            <h1 className="m-0 mt-1 text-[26px] font-extrabold tracking-[-.03em]">
              Inputs
            </h1>
            <p className="m-0 mt-2 max-w-[620px] text-[13.5px] leading-[1.5] text-[var(--muted)]">
              These are the documents and data rows currently driving the
              {` ${projectLabel} project`}: take-off, specification records, notes,
              source links, and the latest captured project run.
            </p>
          </div>
          <button
            type="button"
            onClick={onOpenTender}
            className="flex cursor-pointer items-center gap-2 rounded-[10px] bg-[var(--ink)] px-4 py-[11px] text-[13px] font-bold text-[var(--onSolid)]"
          >
            Review tender text
          </button>
        </div>

        <div className="mt-[18px] grid gap-[11px] sm:grid-cols-2 xl:grid-cols-4">
          {view.stats.map((stat) => (
            <div
              key={stat.id}
              className="rounded-[13px] border border-[var(--line)] bg-[var(--surface)] px-[17px] py-3.5"
            >
              <div className="text-[24px] font-extrabold tracking-[-.03em]">
                {plainStatValue(stat.value)}
              </div>
              <div className="mt-0.5 text-[11.5px] font-semibold text-[var(--muted2)]">
                {plainStatLabel(stat.label)}
              </div>
            </div>
          ))}
        </div>

        <TenderIngestionCta
          selectedApartmentTypeId={selectedApartmentTypeId}
          onOpenTender={onOpenTender}
        />

        <div className="mx-0.5 mb-3 mt-[26px] text-[10.5px] font-bold uppercase tracking-[.12em] text-[var(--faint)]">
          Documents
        </div>
        <div className="flex flex-col gap-3" data-showme="inputs-documents">
          {documents.map((document) => {
            const kindTone = documentKindTone[document.kind] ?? {
              bg: "var(--chip)",
              text: "var(--muted2)"
            };
            const statusTone = documentStatusTone[document.statusTone];
            const metadata = documentMetaParts(document, view.updatedAt);
            const badges = documentBadges(document);

            return (
              <article
                key={document.id}
                className="rounded-[15px] border border-[var(--line)] bg-[var(--surface)] px-[19px] py-[17px] shadow-[0_20px_42px_-38px_rgba(35,33,30,.78)]"
              >
                <div className="flex items-start gap-3.5">
                  <span
                    className="flex h-11 w-11 flex-none items-center justify-center rounded-[11px] text-[10.5px] font-extrabold tracking-[.02em]"
                    style={{ background: kindTone.bg, color: kindTone.text }}
                  >
                    {document.kind}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[15.5px] font-extrabold tracking-[-.01em]">
                        {document.name}
                      </span>
                      {badges.map((badge) => (
                        <span
                          key={badge}
                          className={
                            badge === "OVERRIDE"
                              ? "rounded-full bg-[#F6EEDD] px-2 py-0.5 text-[9.5px] font-extrabold uppercase tracking-[.05em] text-[#8A6A2E]"
                              : "rounded-full bg-[var(--chip)] px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[.05em] text-[var(--muted)]"
                          }
                        >
                          {badge}
                        </span>
                      ))}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {metadata.map((part) => (
                        <span
                          key={part}
                          className="rounded-md border border-[var(--line2)] bg-[var(--raised2)] px-2 py-1 text-[11px] font-bold text-[var(--muted)]"
                        >
                          {part}
                        </span>
                      ))}
                    </div>
                  </div>
                  <span
                    className="flex-none rounded-full px-[11px] py-1 text-[10.5px] font-bold"
                    style={{ background: statusTone.bg, color: statusTone.text }}
                  >
                    {plainDocumentStatusLabel(document.statusLabel)}
                  </span>
                </div>
                <p className="m-0 mt-[13px] text-[12.5px] leading-[1.5] text-[var(--ink2)]">
                  {document.requirementCount > 0
                    ? `${document.requirementCount} requirement${document.requirementCount === 1 ? "" : "s"} linked to this real input.`
                    : "This input is present, but no requirement rows are linked to it yet."}
                </p>
                <div className="mt-[14px] flex flex-wrap items-center gap-2 border-t border-[var(--line2)] pt-[13px]">
                  <span className="rounded-full bg-[#ECEAF6] px-3 py-1.5 text-xs font-bold text-[#6A5E97]">
                    {document.requirementCount} requirement
                    {document.requirementCount === 1 ? "" : "s"} →
                  </span>
                  <div className="ml-auto flex flex-wrap justify-end gap-1.5">
                    <button
                      type="button"
                      onClick={() => downloadInputDocument(document, view.updatedAt)}
                      className="min-h-8 cursor-pointer rounded-[8px] border border-[var(--line3)] bg-[var(--surface)] px-2.5 text-[11px] font-extrabold text-[var(--ink2)] hover:bg-[var(--chip)]"
                    >
                      Download summary
                    </button>
                    <button
                      type="button"
                      onClick={onOpenTender}
                      className="min-h-8 cursor-pointer rounded-[8px] border border-[var(--line3)] bg-[var(--surface)] px-2.5 text-[11px] font-extrabold text-[var(--ink2)] hover:bg-[var(--chip)]"
                    >
                      Re-pass
                    </button>
                    <button
                      type="button"
                      onClick={onOpenTender}
                      className="min-h-8 cursor-pointer rounded-[8px] border border-[var(--line3)] bg-[var(--surface)] px-2.5 text-[11px] font-extrabold text-[var(--ink2)] hover:bg-[var(--chip)]"
                    >
                      Replace
                    </button>
                  </div>
                </div>
                <TechnicalDetails
                  sources={inputDocumentTechnicalSources(document, view.updatedAt)}
                  checks={inputDocumentTechnicalChecks(document)}
                  rawData={JSON.parse(inputDocumentRawJson(document, view.updatedAt))}
                  downloads={[
                    {
                      kind: "raw-data",
                      onClick: () =>
                        downloadInputDocumentRawData(document, view.updatedAt)
                    }
                  ]}
                  showSummaryHelper={false}
                  className="mt-3"
                  data-testid={`inputs-document-${document.id}-technical-details`}
                />
                <div
                  data-testid={`inputs-document-${document.id}-danger-area`}
                  className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-[#E8C9C3] pt-3"
                >
                  <div className="min-w-0">
                    <div className="text-[10px] font-extrabold uppercase tracking-[.1em] text-[#A23F33]">
                      Danger area
                    </div>
                    <p className="m-0 mt-1 text-[11px] font-semibold leading-[1.4] text-[var(--muted)]">
                      Removing this input also removes it from this on-screen document
                      inventory.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => requestDocumentDelete(document)}
                    className="min-h-8 cursor-pointer rounded-[8px] border border-[#C87567] bg-[var(--surface)] px-3 text-[11px] font-extrabold text-[#A23F33] hover:bg-[#F8E8E5]"
                  >
                    Delete document
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      {pendingDeleteDocument ? (
        <div
          className="fixed inset-0 z-[90] grid place-items-center bg-[rgba(20,20,19,.46)] p-4"
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              cancelDocumentDelete();
            }
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-input-document-title"
            aria-describedby="delete-input-document-effect delete-input-document-instruction"
            className="w-full max-w-[520px] rounded-[16px] border border-[#E0B7AF] bg-[var(--surface)] p-5 shadow-[0_26px_80px_-30px_rgba(0,0,0,.7)]"
          >
            <div className="text-[10.5px] font-extrabold uppercase tracking-[.12em] text-[#A23F33]">
              Danger area
            </div>
            <h2
              id="delete-input-document-title"
              className="m-0 mt-2 break-words text-[20px] font-extrabold tracking-[-.02em] text-[var(--ink)]"
            >
              Delete “{pendingDeleteDocument.name}”?
            </h2>
            <p
              id="delete-input-document-effect"
              className="m-0 mt-2 text-[12.5px] font-semibold leading-[1.5] text-[var(--muted)]"
            >
              This removes the document from the current Inputs inventory. Requirements
              and source links already derived from it may still need review elsewhere
              in the project.
            </p>
            <label className="mt-4 grid gap-1.5">
              <span
                id="delete-input-document-instruction"
                className="text-[11.5px] font-extrabold text-[var(--ink2)]"
              >
                Type the document name exactly to confirm:
              </span>
              <span className="break-all rounded-[7px] bg-[var(--raised)] px-2.5 py-2 text-[11.5px] font-extrabold text-[var(--ink)]">
                {pendingDeleteDocument.name}
              </span>
              <input
                autoFocus
                aria-label="Document name confirmation"
                autoComplete="off"
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.currentTarget.value)}
                className="h-10 rounded-[8px] border border-[var(--line3)] bg-[var(--bg)] px-3 text-[12.5px] font-semibold text-[var(--ink)] outline-none focus:border-[#A23F33]"
              />
            </label>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={cancelDocumentDelete}
                className="h-10 rounded-[8px] border border-[var(--line3)] bg-[var(--surface)] px-4 text-[12px] font-extrabold text-[var(--ink2)] hover:bg-[var(--chip)]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleteConfirmation !== pendingDeleteDocument.name}
                onClick={() => removeDocument(pendingDeleteDocument.id)}
                className="h-10 rounded-[8px] bg-[#A23F33] px-4 text-[12px] font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                Delete “{pendingDeleteDocument.name}”
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

function TenderIngestionCta({
  selectedApartmentTypeId,
  onOpenTender
}: {
  selectedApartmentTypeId: string;
  onOpenTender: () => void;
}) {
  const selectedApartmentLabel = labelFromId(selectedApartmentTypeId);

  return (
    <section className="mt-[22px] rounded-[15px] border border-[#D7E1EA] bg-[#F6FAFD] p-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10.5px] font-bold uppercase tracking-[.12em] text-[#3E6E9A]">
            Add more tender text
          </div>
          <h2 className="m-0 mt-1 text-[19px] font-extrabold tracking-[-.02em]">
            Review pasted tender text
          </h2>
          <p className="m-0 mt-2 max-w-[660px] text-[12.5px] leading-[1.5] text-[var(--muted)]">
            Paste tender text and let the AI identify possible requirements. The
            rule-based preview shows how the specification would change before you
            accept anything. Current apartment type: {selectedApartmentLabel}.
          </p>
        </div>
        <button
          type="button"
          onClick={onOpenTender}
          className="rounded-[10px] bg-[var(--ink)] px-4 py-2.5 text-[12.5px] font-bold text-[var(--onSolid)]"
        >
          Open tender review
        </button>
      </div>
    </section>
  );
}
